import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { trainingService } from '../api/trainingService';
import { buttonClass, ErrorText, inputClass, secondaryButtonClass, StatusPill, useTrainingRoles } from '../components/training/TrainingShared';

const shortDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const Training: React.FC = () => {
  const { isStaff } = useTrainingRoles();
  const navigate = useNavigate();
  const semesters = useQuery({ queryKey: ['training', 'semesters'], queryFn: trainingService.semesters });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => trainingService.createSemester({ name }),
    onSuccess: (s) => navigate(`/training/${s.id}?tab=settings`),
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training</h1>
            <p className="text-sm text-gray-600">Training semesters, trainer sign-up, enrollment and attendance.</p>
          </div>
          {isStaff && !creating && <button className={buttonClass} onClick={() => setCreating(true)}>New semester</button>}
        </div>

        {creating && (
          <form className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-2 items-end" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <label className="block text-sm flex-1 min-w-[16rem]">
              <span className="text-gray-700">Semester name</span>
              <input className={inputClass} autoFocus required placeholder="e.g. Fall/Winter/Spring 2027-2028" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <button className={buttonClass} type="submit" disabled={create.isPending}>Create draft</button>
            <button className={secondaryButtonClass} type="button" onClick={() => setCreating(false)}>Cancel</button>
            <div className="w-full"><ErrorText error={create.error} fallback="Unable to create semester" /></div>
          </form>
        )}

        {semesters.isLoading && <p className="text-gray-600">Loading…</p>}
        <ErrorText error={semesters.error} fallback="Unable to load training semesters" />
        {semesters.data && semesters.data.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 text-gray-600">
            {isStaff ? 'No training semesters yet.' : 'No published training semesters yet.'}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {(semesters.data || []).map((s) => {
            const open = s.trainerSlotsTotal - s.trainerSlotsFilled;
            return (
              <Link key={s.id} to={`/training/${s.id}`} className="block bg-white rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{s.name}</h2>
                  <StatusPill status={s.status} />
                </div>
                <p className="text-sm text-gray-600 mt-1">{shortDate(s.firstSessionAt)} – {shortDate(s.lastSessionAt)} · {s.sessionCount} sessions</p>
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  <span><strong>{s.enrollmentCount}</strong> planters</span>
                  <span className={open > 0 ? 'text-amber-700' : 'text-green-700'}>
                    <strong>{s.trainerSlotsFilled}</strong>/{s.trainerSlotsTotal} trainer slots filled
                  </span>
                  <span>Completion: {s.completionRequiredSessions ?? 'all'} sessions</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default Training;
